/**
 * Validation, request mapping and error wording for the issue report form.
 *
 * Reporters used to fill the "DA Client Issue Report" t.corp template by hand; the service now files
 * that ticket from this form. The limits mirror the service's request rules so reporters see problems
 * before submitting, and every message is an i18n key so the modal owns translation.
 */
import type { TranslateFn } from 'src/arb-wrapper';
import { getErrorsByField } from 'src/utils';
import type {
  CreateIssueReportDto,
  IssueReportErrorBody,
  IssueType,
} from 'src/store/generated/leela-api-generated';

export const ISSUE_TYPES: readonly IssueType[] = [
  'ACCESS_ISSUE',
  'WORKFLOW_FAILURE',
  'CAMPAIGN_SETUP_ISSUE',
  'MISSING_CREATIVES',
  'REFTAG_ISSUE',
  'CREATIVE_BRANDING_ISSUE',
  'OTHER',
];

export const ISSUE_TYPE_LABEL_KEYS: Readonly<Record<IssueType, string>> = {
  ACCESS_ISSUE: 'issue_report_type_access_issue',
  WORKFLOW_FAILURE: 'issue_report_type_workflow_failure',
  CAMPAIGN_SETUP_ISSUE: 'issue_report_type_campaign_setup_issue',
  MISSING_CREATIVES: 'issue_report_type_missing_creatives',
  REFTAG_ISSUE: 'issue_report_type_reftag_issue',
  CREATIVE_BRANDING_ISSUE: 'issue_report_type_creative_branding_issue',
  OTHER: 'issue_report_type_other',
};

const MAX_TEAM_LENGTH = 100;
const MAX_OTHER_ISSUE_TYPE_LENGTH = 100;
const MAX_PROBLEM_DESCRIPTION_LENGTH = 5000;
const MAX_DETAIL_LENGTH = 2000;
const MAX_AFFECTED_CAMPAIGN_COUNT = 100_000;
const MAX_EXAMPLE_CAMPAIGN_LINKS = 10;
const NON_NEGATIVE_INTEGER = /^\d+$/;
const HTTPS_PREFIX = 'https://';
const WHITESPACE = /\s/;
const HTTP_BAD_REQUEST = 400;
const HTTP_FORBIDDEN = 403;
const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_BAD_GATEWAY = 502;
const FIELD_LABEL_SEPARATOR = ', ';

const REJECTED_FIELD_LABEL_KEYS: Readonly<Record<string, string>> = {
  team: 'issue_report_team_label',
  issueType: 'issue_report_issue_type_label',
  otherIssueType: 'issue_report_other_issue_type_label',
  problemDescription: 'issue_report_problem_description_label',
  expectedBehavior: 'issue_report_expected_behavior_label',
  actualBehavior: 'issue_report_actual_behavior_label',
  businessImpact: 'issue_report_business_impact_label',
  affectedCampaignCount: 'issue_report_affected_campaign_count_label',
  exampleCampaignLinks: 'issue_report_example_campaign_links_label',
  attachment: 'issue_report_attachment_label',
};

export interface IssueReportFormValues {
  team: string;
  issueType: IssueType | '';
  otherIssueType: string;
  problemDescription: string;
  expectedBehavior: string;
  actualBehavior: string;
  businessImpact: string;
  affectedCampaignCount: string;
  exampleCampaignLinks: string;
}

export interface TranslatableMessage {
  readonly key: string;
  readonly params?: Readonly<Record<string, string>>;
  /** Params whose values are i18n keys: each key is translated and the list joined when the message renders. */
  readonly keyListParams?: Readonly<Record<string, readonly string[]>>;
}

export type IssueReportFieldErrors = Partial<Record<keyof IssueReportFormValues, TranslatableMessage>>;

export const EMPTY_ISSUE_REPORT_FORM_VALUES: Readonly<IssueReportFormValues> = {
  team: '',
  issueType: '',
  otherIssueType: '',
  problemDescription: '',
  expectedBehavior: '',
  actualBehavior: '',
  businessImpact: '',
  affectedCampaignCount: '',
  exampleCampaignLinks: '',
};

const REQUIRED: TranslatableMessage = { key: 'form_validation_required_field' };

export function validateForm (values: IssueReportFormValues): IssueReportFieldErrors {
  const errors: IssueReportFieldErrors = {
    team: validateText(values.team, { isRequired: true, maxLength: MAX_TEAM_LENGTH }),
    issueType: values.issueType ? undefined : REQUIRED,
    otherIssueType: values.issueType === 'OTHER'
      ? validateText(values.otherIssueType, { isRequired: true, maxLength: MAX_OTHER_ISSUE_TYPE_LENGTH })
      : undefined,
    problemDescription: validateText(values.problemDescription, {
      isRequired: true,
      maxLength: MAX_PROBLEM_DESCRIPTION_LENGTH,
    }),
    expectedBehavior: validateText(values.expectedBehavior, { isRequired: false, maxLength: MAX_DETAIL_LENGTH }),
    actualBehavior: validateText(values.actualBehavior, { isRequired: false, maxLength: MAX_DETAIL_LENGTH }),
    businessImpact: validateText(values.businessImpact, { isRequired: false, maxLength: MAX_DETAIL_LENGTH }),
    affectedCampaignCount: validateAffectedCampaignCount(values.affectedCampaignCount),
    exampleCampaignLinks: validateExampleCampaignLinks(values.exampleCampaignLinks),
  };
  return removeEmptyEntries(errors);
}

export function toRequest (
  values: IssueReportFormValues,
  attachment?: CreateIssueReportDto['attachment'],
): CreateIssueReportDto {
  const links = parseLinks(values.exampleCampaignLinks);
  const count = values.affectedCampaignCount.trim();
  return {
    team: values.team.trim(),
    issueType: values.issueType as IssueType,
    otherIssueType: values.issueType === 'OTHER' ? values.otherIssueType.trim() : undefined,
    problemDescription: values.problemDescription.trim(),
    expectedBehavior: toOptionalText(values.expectedBehavior),
    actualBehavior: toOptionalText(values.actualBehavior),
    businessImpact: toOptionalText(values.businessImpact),
    affectedCampaignCount: count ? Number(count) : undefined,
    exampleCampaignLinks: links.length > 0 ? links : undefined,
    attachment,
  };
}

export function translateMessage (message: TranslatableMessage, t: TranslateFn): string {
  const params: Record<string, string> = { ...message.params };
  for (const [name, keys] of Object.entries(message.keyListParams ?? {})) {
    params[name] = keys.map(key => t(key)).join(FIELD_LABEL_SEPARATOR);
  }
  return Object.keys(params).length > 0 ? t(message.key, params) : t(message.key);
}

export function mapErrorToMessage (status: number | undefined, body: unknown): TranslatableMessage {
  const code = getErrorCode(body);
  if (status === HTTP_BAD_REQUEST) {
    return getRejectedFieldsMessage(body);
  }
  if (status === HTTP_FORBIDDEN && code === 'FEATURE_DISABLED') {
    return { key: 'issue_report_error_feature_disabled' };
  }
  if (status === HTTP_TOO_MANY_REQUESTS && code === 'RATE_LIMITED') {
    return { key: 'issue_report_error_rate_limited' };
  }
  if (status === HTTP_BAD_GATEWAY && code === 'TICKETING_UNAVAILABLE') {
    return { key: 'issue_report_error_ticketing_unavailable' };
  }
  return { key: 'issue_report_error_generic' };
}

interface TextRule {
  readonly isRequired: boolean;
  readonly maxLength: number;
}

function validateText (value: string, rule: TextRule): TranslatableMessage | undefined {
  const trimmed = value.trim();
  if (rule.isRequired && trimmed.length === 0) {
    return REQUIRED;
  }
  return Array.from(trimmed).length > rule.maxLength
    ? { key: 'issue_report_error_too_long', params: { max: rule.maxLength.toLocaleString('en-US') } }
    : undefined;
}

function validateAffectedCampaignCount (value: string): TranslatableMessage | undefined {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return undefined;
  }
  return NON_NEGATIVE_INTEGER.test(trimmed) && Number(trimmed) <= MAX_AFFECTED_CAMPAIGN_COUNT
    ? undefined
    : { key: 'issue_report_error_campaign_count' };
}

function validateExampleCampaignLinks (value: string): TranslatableMessage | undefined {
  const links = parseLinks(value);
  if (links.length > MAX_EXAMPLE_CAMPAIGN_LINKS) {
    return { key: 'issue_report_error_too_many_links' };
  }
  return links.every(isHttpsUrl) ? undefined : { key: 'issue_report_error_link_not_https' };
}

function parseLinks (value: string): string[] {
  return value.split('\n').map(link => link.trim()).filter(link => link.length > 0);
}

/** Mirrors the service's `IsUrl({ protocols: ['https'], require_protocol: true })`, which also requires a dotted host. */
function isHttpsUrl (value: string): boolean {
  if (WHITESPACE.test(value) || !value.toLowerCase().startsWith(HTTPS_PREFIX)) {
    return false;
  }
  try {
    const { hostname } = new URL(value);
    return hostname.includes('.') && !hostname.endsWith('.');
  } catch {
    return false;
  }
}

function toOptionalText (value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function removeEmptyEntries (errors: IssueReportFieldErrors): IssueReportFieldErrors {
  return Object.fromEntries(
    Object.entries(errors).filter(([, message]) => message !== undefined),
  ) as IssueReportFieldErrors;
}

function getErrorCode (body: unknown): IssueReportErrorBody['code'] | undefined {
  return isRecord(body) ? (body.code as IssueReportErrorBody['code'] | undefined) : undefined;
}

function getRejectedFieldsMessage (body: unknown): TranslatableMessage {
  const fieldNames = getRejectedFieldNames(isRecord(body) ? body.message : undefined);
  const labelKeys = Array.from(new Set(fieldNames.flatMap(getRejectedFieldLabelKey)));
  return labelKeys.length > 0
    ? { key: 'issue_report_error_rejected_fields', keyListParams: { fields: labelKeys } }
    : { key: 'issue_report_error_generic' };
}

// Nested errors such as `attachment.filename` are named by their top-level form field.
function getRejectedFieldLabelKey (fieldName: string): string[] {
  const topLevelField = fieldName.split('.')[0];
  return Object.prototype.hasOwnProperty.call(REJECTED_FIELD_LABEL_KEYS, topLevelField)
    ? [REJECTED_FIELD_LABEL_KEYS[topLevelField]]
    : [];
}

// The service sends class-validator errors as objects and attachment errors as "<field> must ..." strings.
function getRejectedFieldNames (message: unknown): string[] {
  if (!Array.isArray(message)) {
    return [];
  }
  const fieldMessages = message.filter(isRecord);
  const textMessages = message.filter((item): item is string => typeof item === 'string');
  const names = [
    ...Object.keys(getErrorsByField(fieldMessages)),
    ...textMessages.map(text => text.split(' ')[0]),
  ];
  return Array.from(new Set(names));
}

function isRecord (value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
